/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import {
    isAdvertisementEntry,
    isAudiobookEntry,
    type Player,
    type PlaylistController,
} from '@amzn/amp-playback'
import { withUserSkip } from '@/playback/metrics/userSkipSignal'
import { guardedPrevious } from '@/playback/skipGuard'

const RESTART_THRESHOLD_SECONDS = 3

/**
 * The transport "previous" gesture shared by the previous button, swipe, keyboard, media keys and
 * the lock screen (hornet's `PlaybackController.back`): within the first 3 s of the current entry
 * step back to the previous one, otherwise restart it. Audiobooks apply the rule to the current
 * chapter (`seekTo` is chapter-relative there). Decides on the content clock and no-ops during an
 * ad break or a queue ad, matching the previous button hornet disables for either.
 */
export async function previousOrRestart(
    player: Player,
    playlistController: PlaylistController
): Promise<void> {
    if (player.ads.adState$.value != null) return
    if (isAdvertisementEntry(playlistController.current.value)) return
    if (isAudiobookEntry(playlistController.current.value)) {
        if (player.sections.getPositionInSection() >= RESTART_THRESHOLD_SECONDS) {
            await player.seekTo(0)
            return
        }
        await withUserSkip('previous', () => player.sections.previousSection())
        return
    }
    if (player.contentTime$.value >= RESTART_THRESHOLD_SECONDS) {
        await player.seekTo(0, { reason: 'restart' })
        return
    }
    if (!guardedPrevious(playlistController)) return
    const previous = await withUserSkip('previous', () => playlistController.skipPrevious())
    if (previous == null) await player.seekTo(0, { reason: 'restart' })
}
